import { ButtonWithMenu } from '@statsbomb/kitbag-components';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DISPLAY_MODE, SETTINGS_PANEL } from '@/consts/searchKeys';
import { useUpdateQueryString } from '@/hooks/useUpdateQueryString';
import { useVisDisplayMode } from '@/hooks/useVisDisplayMode';
import { VisDisplayMode } from '@/types/visualisation';
import { MenuItemWithIcon } from '../menu/MenuItemWithIcon';

const DisplayModeMenuItem = ({
  text,
  onClick,
  isModeSelected,
}: {
  text: string;
  onClick: () => void;
  isModeSelected: boolean;
}) => <MenuItemWithIcon onClick={onClick} text={text} isActive={isModeSelected} />;

export const VisDisplayModeSwitcher = ({
  defaultDisplayMode = VisDisplayMode.BOTH,
}: {
  defaultDisplayMode?: VisDisplayMode;
}) => {
  const { t } = useTranslation('visualisation');
  const [isOpen, setIsOpen] = useState(false);
  const [visDisplayMode] = useVisDisplayMode(defaultDisplayMode);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const updateQueryString = useUpdateQueryString(searchParams);
  const isModeSelected = (mode: VisDisplayMode) => visDisplayMode === mode;
  const selectDisplayMode = (mode: VisDisplayMode) => {
    const paramsToRemove = mode === VisDisplayMode.TABLE ? [SETTINGS_PANEL] : [];
    navigate(`?${updateQueryString([{ key: DISPLAY_MODE, value: mode }], paramsToRemove)}`);
    setIsOpen(false);
  };
  return (
    // Note: testId does not work on ButtonWithMenu, so I have had to wrap this in a div.
    <div data-testid="vis-display-mode-switcher">
      <ButtonWithMenu
        icon="Mode"
        isOpen={isOpen}
        onClick={() => setIsOpen(true)}
        onOutsideClick={() => setIsOpen(false)}
        placement="bottom-end"
        title={t('displayMode')}
        variant="ghost"
      >
        <DisplayModeMenuItem
          text={t('displayTable')}
          isModeSelected={isModeSelected(VisDisplayMode.TABLE)}
          onClick={() => selectDisplayMode(VisDisplayMode.TABLE)}
        />
        <DisplayModeMenuItem
          text={t('displayVis')}
          isModeSelected={isModeSelected(VisDisplayMode.VIS)}
          onClick={() => selectDisplayMode(VisDisplayMode.VIS)}
        />
        <DisplayModeMenuItem
          text={t('displayBoth')}
          isModeSelected={isModeSelected(VisDisplayMode.BOTH)}
          onClick={() => selectDisplayMode(VisDisplayMode.BOTH)}
        />
      </ButtonWithMenu>
    </div>
  );
};
